<script setup lang="ts">
import { ref } from 'vue';
import { useFlowStore } from '@/stores/flow';
import ErrorBanner from './ErrorBanner.vue';

const flow = useFlowStore();
const userName = ref('');
const password = ref('');

async function submit() {
  if (!userName.value.trim() || !password.value) return;
  await flow.signIn(userName.value.trim(), password.value);
}
</script>

<template>
  <section class="screen">
    <h1 class="title">Sign in</h1>
    <p class="lede">
      Use your existing account. We'll add the unlocked course to it once you're in.
    </p>

    <ErrorBanner
      v-if="flow.error"
      :message="flow.error"
      @dismiss="flow.clearError()"
    />

    <form class="form" @submit.prevent="submit">
      <label class="field">
        <span class="label-text">Username</span>
        <input
          v-model="userName"
          type="text"
          autocomplete="username"
          autocapitalize="none"
          spellcheck="false"
          :disabled="flow.busy"
          class="text-input"
        />
      </label>
      <label class="field">
        <span class="label-text">Password</span>
        <input
          v-model="password"
          type="password"
          autocomplete="current-password"
          :disabled="flow.busy"
          class="text-input"
        />
      </label>

      <button
        type="submit"
        class="btn btn-primary btn-block"
        :disabled="flow.busy || !userName.trim() || !password"
      >
        <span v-if="flow.busy" class="spinner" aria-hidden="true"></span>
        <span v-else>Sign in &amp; redeem</span>
      </button>
    </form>

    <div class="row">
      <button class="link-btn" type="button" @click="flow.goBackToChooseAuth()">
        ← Back
      </button>
      <button class="link-btn" type="button" @click="flow.goToCreateAccount()">
        Create account instead →
      </button>
    </div>
  </section>
</template>

<style scoped>
.screen { display: flex; flex-direction: column; }

.title {
  font-family: 'Fraunces', Georgia, serif;
  font-size: clamp(1.6rem, 3vw, 2.1rem);
  letter-spacing: -0.02em;
  color: var(--coco-900);
  margin: 0 0 0.6rem;
  font-weight: 500;
  font-variation-settings: 'SOFT' 50, 'opsz' 96;
}
.lede {
  margin: 0 0 1.25rem;
  color: var(--ink-soft);
  line-height: 1.55;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  margin-bottom: 1rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.label-text {
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--ink);
}
.text-input {
  font: inherit;
  font-size: 1rem;
  padding: 0.7rem 0.9rem;
  border-radius: 10px;
  border: 1.5px solid var(--coco-200);
  background: var(--cream);
  color: var(--coco-900);
  transition: border-color 160ms var(--ease), box-shadow 160ms var(--ease);
}
.text-input:focus {
  outline: none;
  border-color: var(--lime-500);
  box-shadow: 0 0 0 4px var(--lime-100);
}

.row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}
.link-btn {
  background: none;
  border: none;
  color: var(--coco-700);
  cursor: pointer;
  padding: 0.4rem 0;
  font: inherit;
  font-size: 0.88rem;
}
.link-btn:hover { color: var(--coco-900); }
</style>
